import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Badge,
    Button,
    Icon,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    EmptyState,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@elit-hair/elit-ui";
import BRANDS, { type BrandName } from "@elit-hair/brands";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCopy, TbDownload, TbEdit, TbPhone, TbTrash } from "react-icons/tb";
import { toast } from "sonner";

import { useDeletePhoneNumber, usePhoneNumbers } from "@/queries/phone-numbers.queries";
import { ApiPhoneNumber, IPhoneNumber, PhoneNumberStatus } from "@/types/phone.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { PhoneNumbersFormDialog } from "./components/phone-numbers-form-dialog";

function isBrandName(value: string): value is BrandName {
    return value in BRANDS;
}

function renderBrandIcon(brand: string) {
    const brandData = isBrandName(brand) ? BRANDS[brand] : null;
    const displayName = brandData?.displayName ?? brandData?.name ?? brand;
    const initial = displayName.charAt(0).toUpperCase();

    if (brandData?.logoSquare) {
        return (
            <img
                src={brandData.logoSquare}
                alt={displayName}
                title={displayName}
                className="h-6 w-6 rounded-full bg-muted object-cover"
            />
        );
    }

    return (
        <div
            title={displayName}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground"
        >
            {initial}
        </div>
    );
}

export const Route = createFileRoute(APP_ROUTES_FULL.MANAGEMENT_PHONE_NUMBERS)({
    component: ManagementPhoneNumbers,
    staticData: {
        config: {
            hideParentBreadcrumbs: true,
            breadcrumb: {
                title: "phone_numbers",
                to: APP_ROUTES_TO.MANAGEMENT_PHONE_NUMBERS,
            },
            title: "phone_numbers",
        },
    },
});

function ManagementPhoneNumbers() {
    const { t } = useTranslation();
    const FILTER_ALL = "all";
    const FILTER_ACTIVE = "active";
    const FILTER_INACTIVE = "inactive";
    const [filterName, setFilterName] = useState("");
    const [statusFilter, setStatusFilter] = useState(FILTER_ACTIVE);
    const [openDialog, setOpenDialog] = useState(false);
    const [editItem, setEditItem] = useState<ApiPhoneNumber | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [phoneNumberToDelete, setPhoneNumberToDelete] = useState<IPhoneNumber | null>(null);
    const SKELETON_ROW_COUNT = 6;

    const { data: phoneNumbers = [], isLoading, refetch } = usePhoneNumbers();
    const deletePhoneNumberMutation = useDeletePhoneNumber();

    const handleFilterByName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterName(event.target.value);
    };

    const handleOpenDialog = () => {
        setEditItem(undefined);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditItem(undefined);
    };

    const handleEditItem = (item: IPhoneNumber) => {
        const apiPhoneNumber: ApiPhoneNumber = {
            id: item.id,
            phoneNumber: item.number,
            name: item.name || "",
            countries: item.countries || [],
            brands: item.brands || [],
            status: item.isActive ? PhoneNumberStatus.ACTIVE : PhoneNumberStatus.INACTIVE,
        };

        setEditItem(apiPhoneNumber);
        setOpenDialog(true);
    };

    const handleDialogSuccess = () => {
        refetch();
        toast.success("Operation completed successfully");
    };

    // Phone number deletion functions
    const handleOpenDeleteDialog = (phoneNumber: IPhoneNumber) => {
        setPhoneNumberToDelete(phoneNumber);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setPhoneNumberToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleDeletePhoneNumber = async () => {
        if (!phoneNumberToDelete) {
            handleCloseDeleteDialog();
            return;
        }

        try {
            await deletePhoneNumberMutation.mutateAsync(phoneNumberToDelete.id);
            toast.success("Phone number successfully deleted");
            refetch();
        } catch (error) {
            console.error("Error occurred while deleting phone number:", error);
            toast.error("Error occurred while deleting phone number");
        }

        handleCloseDeleteDialog();
    };

    const handleCopyPhoneNumber = async (phoneNumber: string) => {
        try {
            await navigator.clipboard.writeText(phoneNumber);
            toast.success(t("copied_to_clipboard"));
        } catch (error) {
            console.error("Error occurred while copying phone number:", error);
            toast.error("Error occurred while copying phone number");
        }
    };

    const filteredPhoneNumbers = useMemo(() => {
        const searchTerm = filterName.trim().toLowerCase();

        return phoneNumbers.filter((phoneNumber: IPhoneNumber) => {
            if (statusFilter === FILTER_ACTIVE && !phoneNumber.isActive) {
                return false;
            }

            if (statusFilter === FILTER_INACTIVE && phoneNumber.isActive) {
                return false;
            }

            if (!searchTerm) {
                return true;
            }

            const brands = phoneNumber.brands?.join(" ").toLowerCase() ?? "";
            return (
                phoneNumber.number.toLowerCase().includes(searchTerm) ||
                (phoneNumber.name || "").toLowerCase().includes(searchTerm) ||
                brands.includes(searchTerm)
            );
        });
    }, [phoneNumbers, filterName, statusFilter]);

    // Export to CSV function
    const handleExportData = () => {
        if (phoneNumbers.length === 0) {
            toast.error("No data found to export.");
            return;
        }

        const csvColumns = ["Number", "Name", "Status", "Brands", "Countries", "ID"];
        const csvData = phoneNumbers.map((item: IPhoneNumber) => [
            item.number,
            item.name || "-",
            item.isActive ? t("active") : t("inactive"),
            item.brands?.join(", ") || "-",
            item.countries?.join(", ") || "-",
            item.id,
        ]);

        const fileName = `phone-numbers-${DateTime.now().toFormat("yyyy-MM-dd_HH-mm")}`;

        // Manual CSV creation
        const header = csvColumns.join(",");
        const rows = csvData.map((row: string[]) => row.join(","));
        const csvContent = [header, ...rows].join("\n");

        // Download CSV
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        // Create anchor without adding to DOM - this avoids triggering LoadingScreen's click handler
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}.csv`;
        link.click();

        // Clean up the object URL to prevent memory leaks
        setTimeout(() => URL.revokeObjectURL(url), 100);
    };

    return (
        <>
            <div className="flex h-full min-h-0 flex-col gap-4">
                {isLoading ? (
                    <div className="flex flex-wrap items-center gap-3">
                        <Skeleton className="h-10 w-full max-w-sm" />
                        <Skeleton className="h-10 w-[180px]" />
                        <Skeleton className="h-10 w-[110px]" />
                        <Skeleton className="h-10 w-[130px]" />
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center gap-3">
                        <Input
                            placeholder={t("search_by_name_number")}
                            value={filterName}
                            onChange={handleFilterByName}
                            className="max-w-sm"
                        />
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder={t("status")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={FILTER_ACTIVE}>{t("active")}</SelectItem>
                                <SelectItem value={FILTER_INACTIVE}>{t("inactive")}</SelectItem>
                                <SelectItem value={FILTER_ALL}>{t("all")}</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={handleExportData}>
                            <Icon icon={TbDownload} className="mr-2" />
                            {t("export")}
                        </Button>
                        <Button onClick={handleOpenDialog}>
                            <Icon icon={TbPhone} className="mr-2" />
                            {t("new_number")}
                        </Button>
                    </div>
                )}

                <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("brands")}</TableHead>
                                <TableHead>{t("phone_number")}</TableHead>
                                <TableHead>{t("name")}</TableHead>
                                <TableHead>{t("countries")}</TableHead>
                                <TableHead>{t("status")}</TableHead>
                                <TableHead className="text-right">{t("actions")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                                    <TableRow key={`phone-number-skeleton-${index}`}>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Skeleton className="h-6 w-6 rounded-full" />
                                                <Skeleton className="h-6 w-6 rounded-full" />
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton className="h-4 w-32" />
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton className="h-4 w-28" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Skeleton className="h-5 w-12 rounded-md" />
                                                <Skeleton className="h-5 w-12 rounded-md" />
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Skeleton className="h-5 w-16 rounded-md" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-1">
                                                <Skeleton className="h-8 w-8 rounded-md" />
                                                <Skeleton className="h-8 w-8 rounded-md" />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : filteredPhoneNumbers.length === 0 ? (
                                <TableRow>
                                    <EmptyState variant="table" title="no_phone_numbers_found" colSpan={6} />
                                </TableRow>
                            ) : (
                                filteredPhoneNumbers.map((phoneNumber) => (
                                    <TableRow key={phoneNumber.id}>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {phoneNumber.brands
                                                    ?.slice(0, 2)
                                                    .map((brand) => (
                                                        <div key={brand}>{renderBrandIcon(brand)}</div>
                                                    ))}
                                                {phoneNumber.brands && phoneNumber.brands.length > 2 ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Badge
                                                                label={`+${phoneNumber.brands.length - 2}`}
                                                                variant="outline"
                                                            />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <div className="flex flex-wrap gap-1">
                                                                {phoneNumber.brands.slice(2).map((brand: string) => (
                                                                    <div key={brand}>{renderBrandIcon(brand)}</div>
                                                                ))}
                                                            </div>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                ) : null}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <span className="font-medium">{phoneNumber.number}</span>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => void handleCopyPhoneNumber(phoneNumber.number)}
                                                        >
                                                            <Icon icon={TbCopy} />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t("copied_to_clipboard")}</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                        <TableCell>{phoneNumber.name || "-"}</TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {phoneNumber.countries?.map((country) => (
                                                    <Badge key={country} label={country} variant="outline" />
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {phoneNumber.isActive ? (
                                                <Badge label={t("active")} variant="success" />
                                            ) : (
                                                <Badge label={t("inactive")} variant="danger" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-1">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleEditItem(phoneNumber)}
                                                        >
                                                            <Icon icon={TbEdit} />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t("edit")}</TooltipContent>
                                                </Tooltip>

                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleOpenDeleteDialog(phoneNumber)}
                                                        >
                                                            <Icon icon={TbTrash} color="red" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t("delete")}</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Delete confirmation dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("delete_phone_number")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("are_you_sure_you_want_to_delete_the_number", {
                                number: phoneNumberToDelete?.number,
                            })}
                            {t("this_action_cannot_be_undone")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCloseDeleteDialog}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeletePhoneNumber}
                            className="bg-destructive text-destructive-foreground"
                        >
                            {t("delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Form Dialog */}
            <PhoneNumbersFormDialog
                open={openDialog}
                onClose={handleCloseDialog}
                editItem={editItem}
                onSuccess={handleDialogSuccess}
            />
        </>
    );
}
